import { useMutation, useQuery } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';

export const useCreateRFID = (operatorId, options = {}) => {
  return useMutation({
    mutationKey: ['createRFID'],
    mutationFn: async (data) => {
      const response = await httpService.post(`rfid/create`, data);
      return response?.data;
    },
    enabled: !!operatorId,
    ...options,
  });
};

export const useFetchRfidList = (operatorId, page, search, options = {}) => {
  return useQuery({
    queryKey: ['fetch-RFID', operatorId],
    queryFn: async () => {
      const url = search
        ? `/rfid/list?limit=10&offset=${page}&search=${search}`
        : `/rfid/list?limit=10&offset=${page}`;
      const response = await httpService.post(url, {
        operatorId,
      });

      return response.data.data;
    },
    ...options,
    refetchOnWindowFocus: false,
  });
};

export const useFetchCurrentReport = ({ token, clientId, pageParam }) => {
  return useQuery({
    queryKey: ['fetch-RFID', clientId, pageParam],
    queryFn: async () => {
      const response = await httpService.get(
        `/rfid/list?clientId=${clientId}&offset=${pageParam}`,
        token,
        null
      );

      return response.data.data;
    },
    keepPreviousData: true,
    enabled: !!(token && clientId !== undefined && clientId !== null),
    refetchOnWindowFocus: false,
  });
};

export const useGetSingleRFID = ({ id, operatorId }) => {
  return useQuery({
    queryKey: ['getRFID', id],
    queryFn: async () => {
      const response = await httpService.post(`rfid/get-single/${id}`, {
        operatorId,
      });
      // Support both { data: { data: rfid } } and { data: rfid } response shapes
      return response?.data?.data ?? response?.data;
    },
    enabled: !!id,
  });
};

export const useAddCharger = (token) => {
  return useMutation({
    mutationKey: ['AddChargerTORFID'],
    mutationFn: async ({ clientId, chargerId, RFID }) => {
      const response = await httpService.post('/rfid/add-charger', token, {
        clientId,
        chargerId,
        RFID,
      });

      return response.data.data;
    },
  });
};

/** Block/unblock is done via update with status: 'Blocked' | 'Accepted' */

export const useRemoveRFID = () => {
  return useMutation({
    mutationKey: ['RemoveRFID'],
    mutationFn: async ({ id, operatorId }) => {
      const response = await httpService.post(`rfid/delete/${id}`, {
        operatorId,
      });
      return response?.data;
    },
  });
};

export const useUpdateRFID = (id, options = {}) => {
  return useMutation({
    mutationKey: ['UpdateRFID', id],
    mutationFn: async (payload) => {
      const targetId = id ?? payload?.id;
      if (!targetId) throw new Error('RFID id is required for update');
      const body = payload?.id != null ? { ...payload, id: undefined } : payload;
      const response = await httpService.post(`rfid/update/${targetId}`, body);
      return response?.data;
    },
    ...options,
  });
};

export const useToggleBlockUnblockRFID = (id, options = {}) => {
  return useMutation({
    mutationKey: ['ToggleBlockUnblockRFID', id],
    mutationFn: async (payload) => {
      const targetId = id ?? payload?.id;
      if (!targetId) throw new Error('RFID id is required for toggle block/unblock');
      const { operatorId, status } = payload;
      const response = await httpService.post(`rfid/toggle-block-unblock/${targetId}`, {
        operatorId,
        status,
      });
      return response?.data;
    },
    ...options,
  });
};

export const useAddBalanceRFID = (id, options = {}) => {
  return useMutation({
    mutationKey: ['AddBalanceRFID', id],
    mutationFn: async (payload) => {
      const targetId = id ?? payload?.id;
      if (!targetId) throw new Error('RFID id is required for add-balance');
      const { operatorId, balance } = payload?.id != null ? payload : { ...payload };
      const response = await httpService.post(`rfid/add-balance/${targetId}`, {
        operatorId,
        balance,
      });
      return response?.data;
    },
    ...options,
  });
};

export const useFetchRFIDTransactions = (payload, options = {}) => {
  return useQuery({
    queryKey: ['fetch-rfid-transactions', payload],
    queryFn: async () => {
      const response = await httpService.post('/rfid/transaction-list', payload);
      return response?.data?.data;
    },
    keepPreviousData: true,
    ...options,
  });
};

/**
 * Select RFID list for dropdowns (e.g. DebouncedSelect).
 * API: GET /rfid/select-rfid?operatorId=...&search=...
 */
export const useSelectRfid = (operatorId, search, options = {}) => {
  return useQuery({
    queryKey: ['rfid', 'select-rfid', operatorId, search],
    queryFn: async () => {
      const params = new URLSearchParams({ operatorId });
      if (search) params.set('search', search);
      const response = await httpService.get(`/rfid/select-rfid?${params.toString()}`);
      const list = response?.data?.data?.rfids ?? response?.data?.rfids ?? [];
      return Array.isArray(list)
        ? list.map((item) => ({
            label: item.RFID ?? item.rfid ?? item.name ?? item._id,
            value: item._id,
            ...item,
          }))
        : [];
    },
    enabled: !!operatorId,
    ...options,
  });
};
export const useGenerateRFIDUsageReport = (options = {}) => {
  return useMutation({
    mutationKey: ['GENERATE_RFID_USAGE_REPORT'],
    mutationFn: async (payload) => {
      const response = await httpService.post('report/custom-report', payload);
      return response?.data;
    },
    ...options,
  });
};
